import BrowserInteractionTime from 'browser-interaction-time';

import { BROADCAST_CHANNEL_PREFIX, DEFAULT_IDLE_TIME, INTERVAL_TIME } from '../../common/constants';
import { type RemoteActionsResponse } from '../../common/types';
import { getRemoteActions } from '../remote-actions';

type RemoteActionPolledMessage = {
	messageType: 'remote-action-polled';
	response: RemoteActionsResponse;
};

const createBroadcastChannel = (channelName: string) => {
	try {
		// eslint-disable-next-line compat/compat
		return new BroadcastChannel(channelName);
	} catch {
		return undefined;
	}
};

export enum PollingState {
	POLLING = 'polling', // the service is actively polling
	SUSPENDED = 'suspended', // the service is not polling. It will still process messages from the broadcast channel
	DESTROYED = 'destroyed', // the service is destroyed and cannot be started again
}

export class RemoteActionsPollingService {
	private pollingState: PollingState = PollingState.SUSPENDED;
	private pollingInterval: number;
	private broadcastChannel?: BroadcastChannel;
	private lastResponse?: RemoteActionsResponse;
	private intervalInstance?: NodeJS.Timeout;
	private browserInteractionTime?: BrowserInteractionTime;
	private lastUpdateReceivedAt: number = Date.now();

	constructor(
		private endpoint: string,
		private product: string,
		private onRemoteActionsUpdate: (response: RemoteActionsResponse) => void,
		private onPollingError: (error: unknown) => void,
		pollingInterval?: number | undefined,
		idleTime?: number | undefined,
	) {
		this.pollingInterval = pollingInterval || INTERVAL_TIME;
		this.broadcastChannel = createBroadcastChannel(`${BROADCAST_CHANNEL_PREFIX}-${product}`);
		this.broadcastChannel?.addEventListener(
			'message',
			(event: MessageEvent<RemoteActionPolledMessage>) => {
				if (event.data.messageType === 'remote-action-polled') {
					this.lastUpdateReceivedAt = Date.now();
					if (
						!this.lastResponse?.lastUpdatedAt ||
						this.lastResponse.lastUpdatedAt < event.data.response.lastUpdatedAt
					) {
						this.lastResponse = event.data.response;
						this.onRemoteActionsUpdate(event.data.response);
					}
				}
			},
		);
		this.intervalInstance = setInterval(() => {
			if (this.pollingState === PollingState.POLLING) {
				this.fetchRemoteActions();
			}
		}, this.pollingInterval || INTERVAL_TIME);
		this.browserInteractionTime = new BrowserInteractionTime({
			idleTimeoutMs: idleTime || DEFAULT_IDLE_TIME,
			stopTimerOnTabchange: false,
			browserTabActiveCallbacks: [this.onActive.bind(this)],
			browserTabInactiveCallbacks: [this.onInactive.bind(this)],
			activeCallbacks: [this.onActive.bind(this)],
			idleCallbacks: [this.onInactive.bind(this)],
		});
		this.browserInteractionTime.startTimer();
		this.start();
	}

	private onActive() {
		if (this.pollingState === PollingState.SUSPENDED) {
			this.start();
		}
	}

	private onInactive() {
		if (this.pollingState === PollingState.POLLING) {
			this.suspend();
		}
	}

	private async fetchRemoteActions() {
		try {
			if (Date.now() - this.lastUpdateReceivedAt >= this.pollingInterval) {
				const response = await getRemoteActions(this.endpoint, this.product);
				if (response) {
					this.lastUpdateReceivedAt = Date.now();
					if (
						!this.lastResponse?.lastUpdatedAt ||
						this.lastResponse?.lastUpdatedAt < response.lastUpdatedAt
					) {
						this.lastResponse = response;
						this.onRemoteActionsUpdate(response);
					}
					if (this.broadcastChannel) {
						this.broadcastChannel.postMessage({ response, messageType: 'remote-action-polled' });
					}
				}
			}
		} catch (error) {
			this.onPollingError(error);
		}
	}

	start() {
		if (this.pollingState === PollingState.DESTROYED) {
			throw new Error('RemoteActionService instance was destroyed and cannot be started again');
		}
		this.pollingState = PollingState.POLLING;

		// fetch the remote actions immediately (if the last update was more than the polling interval ago)
		this.fetchRemoteActions();
	}

	suspend() {
		this.pollingState = PollingState.SUSPENDED;
	}

	destroy() {
		if (this.pollingState !== PollingState.DESTROYED) {
			this.suspend();
			clearInterval(this.intervalInstance);
			this.intervalInstance = undefined;
			this.browserInteractionTime?.destroy();
			this.browserInteractionTime = undefined;
			this.broadcastChannel?.close();
			this.broadcastChannel = undefined;
			this.pollingState = PollingState.DESTROYED;
		}
	}

	getState() {
		return this.pollingState;
	}
}
